/**
 * Bandsintown API Service
 *
 * Fetches artist events from Bandsintown API
 * API Docs: https://artists.bandsintown.com/support/api-installation
 *
 * Note: The Bandsintown API is free and doesn't require authentication for basic event lookups.
 * Artist profile: https://www.bandsintown.com/a/15378096-sirius-company
 */

const BANDSINTOWN_API_BASE = 'https://rest.bandsintown.com';
// Use environment variable for API key - must be set in Vercel
const APP_ID = process.env.REACT_APP_BANDSINTOWN_API_KEY;

// Default artist - Sirius Company Ft. Scooby Da God Son
// Try different variations of the artist name
const DEFAULT_ARTIST = 'Sirius Company';

/**
 * Fetch upcoming events for an artist from Bandsintown
 * @param {string} artistName - The artist name as it appears on Bandsintown
 * @returns {Promise<Array>} Array of event objects
 */
export async function fetchArtistEvents(artistName = DEFAULT_ARTIST) {
  try {
    // Encode artist name for URL
    const encodedArtist = encodeURIComponent(artistName);

    const response = await fetch(
      `${BANDSINTOWN_API_BASE}/artists/${encodedArtist}/events?app_id=${APP_ID}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Artist "${artistName}" not found on Bandsintown`);
        return [];
      }
      throw new Error(`Bandsintown API error: ${response.status}`);
    }

    const events = await response.json();

    // Handle case where API returns an error object instead of array
    if (!Array.isArray(events)) {
      console.warn('Bandsintown returned non-array response:', events);
      return [];
    }

    return events.map(transformBandsintownEvent);
  } catch (error) {
    console.error('Error fetching from Bandsintown:', error);
    return [];
  }
}

/**
 * Get artist info from Bandsintown
 * @param {string} artistName - The artist name
 * @returns {Promise<Object|null>} Artist info or null
 */
export async function fetchArtistInfo(artistName = DEFAULT_ARTIST) {
  try {
    const encodedArtist = encodeURIComponent(artistName);

    const response = await fetch(
      `${BANDSINTOWN_API_BASE}/artists/${encodedArtist}?app_id=${APP_ID}`
    );

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching artist info:', error);
    return null;
  }
}

/**
 * Transform Bandsintown event to our Firestore format
 * @param {Object} event - Bandsintown event object
 * @returns {Object} Transformed event for Firestore
 */
function transformBandsintownEvent(event) {
  const venue = event.venue || {};
  const eventDate = new Date(event.datetime);

  return {
    // Core info
    title: event.title || `${event.artist?.name || 'Sirius Company'} Live`,
    date: eventDate.toISOString().split('T')[0], // YYYY-MM-DD format
    time: eventDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),

    // Venue info
    venue: venue.name || 'TBA',
    address: venue.street_address || '',
    city: venue.city || '',
    state: venue.region || '',
    country: venue.country || 'United States',

    // Ticket info
    ticketUrl: event.url || event.offers?.[0]?.url || '',
    ticketPrice: null, // Bandsintown doesn't provide price

    // Status
    status: event.offers?.length > 0 ? 'on_sale' : 'upcoming',

    // Metadata
    source: 'bandsintown',
    bandsintownId: event.id,
    artistName: event.artist?.name || 'Sirius Company',
    lineup: event.lineup || [],
    description: event.description || '',

    // Timestamps
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

/**
 * Check if an event already exists in Firestore (by bandsintownId or matching date/venue)
 * @param {Object} event - Event to check
 * @param {Array} existingEvents - Array of existing Firestore events
 * @returns {boolean} True if event exists
 */
export function eventExists(event, existingEvents) {
  return existingEvents.some(existing => {
    // Check by Bandsintown ID
    if (existing.bandsintownId && existing.bandsintownId === event.bandsintownId) {
      return true;
    }
    // Check by date + venue combination
    if (existing.date === event.date && existing.venue === event.venue) {
      return true;
    }
    return false;
  });
}

/**
 * Merge Bandsintown events with existing Firestore events
 * Returns only new events that don't already exist
 * @param {Array} bandsintownEvents - Events from Bandsintown
 * @param {Array} firestoreEvents - Existing events in Firestore
 * @returns {Array} New events to add
 */
export function getNewEvents(bandsintownEvents, firestoreEvents) {
  return bandsintownEvents.filter(event => !eventExists(event, firestoreEvents));
}

const bandsintownService = {
  fetchArtistEvents,
  fetchArtistInfo,
  eventExists,
  getNewEvents,
};

export default bandsintownService;
